import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import {
  EmailConnectionStatusEnum,
  type TransactionFilter,
} from "@/__generated__/graphql";
import { Queries } from "@/constants";
import { useBackendQuery } from "@/hooks/useBackendQuery";
import { useScope } from "@/hooks/useScope";
import { State } from "@/state";
import { identifierAndTypenameToId } from "@/objects/@utils";

import {
  buildChangelistProposalPresentation,
  changelistProposalDisplayTitle,
  suggestionConclusion,
} from "./TriageChangelistProposalRow";
import { TriageEmailConnectionSearchResultsDialog } from "./TriageEmailConnectionSearchResultsDialog";
import {
  TriageOptionList,
  documentToTriageListItem,
  transactionToTriageListItem,
  type TriageOptionListItem,
} from "./TriageSuggestionsPanel";
import {
  accountToListItem,
  documentCatalogFilter,
  transactionCatalogFilter,
} from "./TriageTabOptionsPanel";
import { type TriageJobEnum } from "./TriageView.keys";
import { TRIAGE_TAB_CATALOG_CONFIG } from "./TriageView.resolution";
import {
  toggleLinesSuggestion,
  useTriageDetailContext,
  type TriageDetailContextValue,
  type TriageGroupMenu,
  type TriageSuggestionSelections,
  type TriageSuggestionStub,
} from "./TriageView.context";

/** The kinds a payment's suggestions are of, each a button in the toolbar, and the jobs each takes. */
export const TRIAGE_SUGGESTION_GROUPS: NonNullable<
  TriageOptionListItem["group"]
>[] = [
  {
    id: "match",
    title: "Match",
    jobs: ["MATCH_DOCUMENT"],
    tab: "document",
    searches: "documents",
  },
  {
    id: "duplicate",
    title: "Duplicates",
    jobs: ["DUPLICATE"],
    tab: "transactions",
    searches: "duplicates",
  },
  {
    id: "transfer",
    title: "Transfer",
    jobs: ["TRANSFER", "PAIR"],
    tab: "transactions",
    searches: "transfers",
  },
  {
    id: "post",
    title: "Post",
    jobs: ["CATEGORIZE", "SPLIT"],
    tab: "categorize",
    searches: "accounts",
  },
];

/** No chips: one for all, so what is searched for is not worked out afresh each time it is drawn. */
const NO_TERMS: Record<string, string[]> = {};

/** What a card shows of what it found for what is typed, before the way to the rest. */
const FOUND_SHOWN = 5;

const TRIAGE_SUGGESTION_JOB_ORDER: Array<TriageJobEnum> = [
  "MATCH_DOCUMENT",
  "CATEGORIZE",
  "DUPLICATE",
  "SPLIT",
  "PAIR",
  "TRANSFER",
];

type SuggestionLine = {
  amount?: string | number;
  description?: string;
  chartedAccount?: {
    identifier?: string | null;
    name?: string | null;
  } | null;
};

type SuggestionLineSource = Pick<TriageSuggestionStub, "lines">;

export function hasSuggestionLines(suggestion: SuggestionLineSource): boolean {
  return (suggestion.lines?.length ?? 0) > 0;
}

/** @deprecated use hasSuggestionLines */
export function asChangelist(
  suggestion: SuggestionLineSource,
): { lines: SuggestionLine[] } | null {
  if (!hasSuggestionLines(suggestion)) return null;
  return { lines: (suggestion.lines ?? []) as SuggestionLine[] };
}

function changelistProposals(
  suggestions: TriageSuggestionStub[] | null | undefined,
): TriageSuggestionStub[] {
  return (suggestions ?? []).filter(hasSuggestionLines);
}

function suggestionEntityScope(
  suggestion: TriageSuggestionStub,
  triage: TriageDetailContextValue["triage"],
): TriageOptionListItem["entityScope"] {
  const entityName = triage.entity?.name?.trim();
  if (!entityName) {
    return undefined;
  }

  const entity = {
    nodeId: triage.entity?.id ?? null,
    label: entityName,
  };

  // Another business's, when its own lines are: nothing else says whose it is.
  const other = (suggestion.lines ?? []).find(
    (line) => line.entity?.identifier !== triage.entity?.identifier,
  )?.entity;
  if (other?.name) {
    return {
      entity,
      belongsTo: { nodeId: other.id ?? null, label: other.name },
    };
  }

  return { entity };
}

function proposalToListItem(
  suggestion: TriageSuggestionStub,
  triage: TriageDetailContextValue["triage"],
  selectedId: string | null,
): TriageOptionListItem | null {
  const presentation = buildChangelistProposalPresentation(suggestion, triage);
  const entityScope = suggestionEntityScope(suggestion, triage);
  const tags = (suggestion.ruleSuggestionTagOptions ?? []).flatMap((option) => {
    const tag = option.tag;
    if (!tag?.identifier || !tag.name?.trim()) {
      return [];
    }
    return [
      {
        identifier: tag.identifier,
        name: tag.name.trim(),
        color: tag.color,
      },
    ];
  });
  return {
    key: `lines:${suggestion.identifier}`,
    sectionId: "lines",
    selectionMode: "single",
    entityScope,
    title: changelistProposalDisplayTitle(
      presentation.job,
      presentation.headline,
    ),
    tags: tags.length > 0 ? tags : undefined,
    confidence: Number(suggestion.confidence ?? 0),
    suggestion,
    proposalGrouped: true,
    selected: selectedId === suggestion.identifier,
  };
}

function buildChangelistProposalItems(
  proposals: TriageSuggestionStub[],
  triage: TriageDetailContextValue["triage"],
  selections: TriageSuggestionSelections,
): TriageOptionListItem[] {
  const byJob = new Map<string, TriageSuggestionStub[]>();

  for (const proposal of proposals) {
    const presentation = buildChangelistProposalPresentation(proposal, triage);
    const job = presentation.job;
    const existing = byJob.get(job) ?? [];
    existing.push(proposal);
    byJob.set(job, existing);
  }

  const items: TriageOptionListItem[] = TRIAGE_SUGGESTION_JOB_ORDER.flatMap(
    (job) => {
      // Rules that come to the same conclusion are one row: the first, most
      // confident, with the others counted on its chip.
      const byConclusion = new Map<string, TriageSuggestionStub[]>();
      for (const suggestion of byJob.get(job) ?? []) {
        const liveSuggestion =
          triage.suggestions?.find(
            (row) => row.identifier === suggestion.identifier,
          ) ?? suggestion;
        const conclusion = suggestionConclusion(liveSuggestion);
        byConclusion.set(conclusion, [
          ...(byConclusion.get(conclusion) ?? []),
          liveSuggestion,
        ]);
      }
      return [...byConclusion.values()].flatMap(([first, ...agreeing]) => {
        const item = proposalToListItem(
          first,
          triage,
          selections.linesSuggestionIdentifier,
        );
        return item
          ? [
              {
                ...item,
                // Picked as any of those that came to the same is picked.
                selected:
                  item.selected ||
                  agreeing.some(
                    (other) =>
                      other.identifier === selections.linesSuggestionIdentifier,
                  ),
              },
            ]
          : [];
      });
    },
  );

  // Each a card of its own, by kind - none headed: its icon says its kind -
  // the keyboard on it on its one stop.
  return TRIAGE_SUGGESTION_GROUPS.flatMap((group) =>
    items
      .filter((item) =>
        group.jobs.includes(
          buildChangelistProposalPresentation(item.suggestion!, triage).job,
        ),
      )
      .map(
        (item): TriageOptionListItem => ({
          ...item,
          selected: false,
          group,
          alternatives: [item],
        }),
      ),
  );
}

/** Suggestions tab: keyboard-navigable changelist proposal list. */
export function TriageSuggestionsTabPanel({
  suggestions,
  terms = NO_TERMS,
  searchesMailboxes = true,
}: {
  suggestions: TriageSuggestionStub[];
  /**
   * The words searched for, made chips in the match panel's toolbar: by card,
   * and under ``all`` those every card is searched for.
   */
  terms?: Record<string, string[]>;
  /** Whether the words are looked for in the mailboxes too - not once their chip is let go. */
  searchesMailboxes?: boolean;
}) {
  const {
    activeOptionKey,
    setActiveOptionKey,
    setSelection,
    selections,
    suggestionsEngaged,
    isProcessing,
    triage,
    rerunningSuggestions,
    importingEmailConnectionSearchResult,
    setImportingEmailConnectionSearchResult,
    reconcilingManually,
    registerStepSuggestions,
    registerCatalogChartedAccounts,
    registerCatalogDocuments,
    registerCatalogTransactions,
    searchMailbox,
    searching,
    searchFailure,
    journalSettlement,
    setGroupMenus,
  } = useTriageDetailContext();

  // Every match is on offer, its documents' payments said on its card: one
  // another payment pays is paid by both where this fits in what is left of
  // it, else moved here (``TriageSuggestionContext``); and one marked paid by
  // hand with no payment of its own is paid by this one in the mark's place.
  const proposals = useMemo(
    () => changelistProposals(suggestions),
    [suggestions],
  );

  // The words each card is searched for - its own chips, and those for every
  // card - kept with it as the keyboard moves on and comes back; each looks
  // in what it takes: documents to match it to, payments of its amount it may
  // be a copy of, payments the other way it may be moved to, accounts to post
  // it to.
  const searches = useMemo(() => {
    const byGroup: Record<string, { words: string[]; term: string }> = {};
    for (const group of TRIAGE_SUGGESTION_GROUPS) {
      const words = [...(terms.all ?? []), ...(terms[group.id] ?? [])];
      byGroup[group.id] = { words, term: words.join(" ") };
    }
    return byGroup;
  }, [terms]);
  const business = triage.entity?.identifier ?? null;
  // Its accounts are those of the business its cost is in: where its Move
  // says it is for, not the one that paid it.
  const bookedIn = journalSettlement?.settlementEntityIdentifier ?? business;
  const scope = useScope();
  // What its cards are drawn with besides its suggestions, as the payment's
  // detail asks for them (``TriageDetail``), with the payment, the panel's
  // one spinner till they are in: read here from what it has, never asked
  // again. Its business's mailboxes, whatever it is offered: the card of ways
  // to its receipt is drawn whole, with the suggestions, not grown after them.
  const mailboxes = useBackendQuery(Queries.EMAIL_CONNECTIONS_QUERY, {
    variables: { entityIdentifiers: business ? [business] : [] },
    fetchPolicy: "cache-first",
    skip: !business,
  });
  // The mailboxes a search may look in for its business - theirs, and those
  // shared with them there.
  const searchableMailboxes = useMemo(
    () =>
      (mailboxes.data?.emailConnections?.data ?? []).filter((mailbox) =>
        mailbox.assignments.some(
          (given) => given.entity?.identifier === business,
        ),
      ),
    [business, mailboxes.data],
  );
  const looksOn = (groupId: string) =>
    Boolean(business && searches[groupId].term);
  const foundDocuments = useBackendQuery(Queries.DOCUMENTS_CONNECTION_QUERY, {
    variables: {
      entityIdentifiers: business ? [business] : [],
      first: FOUND_SHOWN + 1,
      // Every word, each where the catalog looks.
      filter: { all: searches.match.words.map(documentCatalogFilter) },
    },
    skip: !looksOn("match"),
    fetchPolicy: "cache-first",
  });
  const foundCopies = useBackendQuery(Queries.TRANSACTIONS_CONNECTION_QUERY, {
    variables: {
      entityIdentifiers: business ? [business] : [],
      // The payment itself is one of them.
      first: FOUND_SHOWN + 2,
      filter: {
        all: [
          { object: { financialAccount: { isNull: false } } },
          { field: { amount: { exact: Number(triage.amount ?? 0) } } },
          ...searches.duplicate.words.map((word) => ({
            any: [
              { field: { description: { iContains: word } } },
              { search: word },
            ],
          })),
        ],
      } as TransactionFilter,
    },
    skip: !looksOn("duplicate"),
    fetchPolicy: "cache-first",
  });
  const foundTransfers = useBackendQuery(
    Queries.TRANSACTIONS_CONNECTION_QUERY,
    {
      variables: {
        // Moved to any of the viewer's businesses.
        entityIdentifiers: (scope.entities ?? []).map(
          (entity) => entity.identifier,
        ),
        first: FOUND_SHOWN + 1,
        filter: {
          all: [
            ...searches.transfer.words.map(transactionCatalogFilter),
            {
              field: {
                amount: Number(triage.amount ?? 0) < 0 ? { gt: 0 } : { lt: 0 },
              },
            },
          ],
        } as TransactionFilter,
      },
      skip: !looksOn("transfer"),
      fetchPolicy: "cache-first",
    },
  );
  const foundAccounts = useBackendQuery(
    Queries.TRIAGE_CHARTED_ACCOUNT_CATALOG_QUERY,
    {
      variables: { entityIdentifiers: bookedIn ? [bookedIn] : [] },
      skip: !looksOn("post"),
      fetchPolicy: "cache-first",
    },
  );
  // What the words could be posted as, best first: the business's own
  // accounts and categories, and the catalogue's it has not made yet - found
  // by meaning, not only by the letters typed.
  const foundWays = useBackendQuery(Queries.WAYS_TO_BOOK_QUERY, {
    variables: { entityIdentifier: bookedIn ?? "", words: searches.post.term },
    skip: !looksOn("post"),
    fetchPolicy: "cache-first",
  });
  // Each card's finds: the first few, as stops of the card, then the way to
  // the rest - its whole list, to pick from by hand.
  const foundByGroup = useMemo((): Record<string, TriageOptionListItem[]> => {
    const compareAmount = triage.amount != null ? Number(triage.amount) : null;
    const shown = (
      groupId: string,
      rows: TriageOptionListItem[],
      hasMore: boolean,
    ): TriageOptionListItem[] =>
      !searches[groupId].term
        ? []
        : rows.length === 0
          ? // Nothing found is a row as what is found is: the whole list,
            // to look through by hand.
            [
              {
                key: `more:${groupId}`,
                sectionId: "more",
                selectionMode: "single" as const,
                title: `Nothing found for ${searches[groupId].words
                  .map((word) => `“${word}”`)
                  .join(" ")}`,
                description: "Look through them all",
                selected: false,
                isFound: true,
              },
            ]
          : [
              ...rows
                .slice(0, FOUND_SHOWN)
                .map((row) => ({ ...row, isFound: true })),
              // The way to the rest.
              ...(hasMore || rows.length > FOUND_SHOWN
                ? [
                    {
                      key: `more:${groupId}`,
                      sectionId: "more",
                      selectionMode: "single" as const,
                      title: "See all",
                      selected: false,
                      isFound: true,
                    },
                  ]
                : []),
            ];
    const payments = (
      connection: NonNullable<
        typeof foundCopies.data
      >["transactionsConnection"]["data"],
    ) =>
      (connection?.edges ?? [])
        .map((edge) => edge.node)
        .filter((payment) => payment.identifier !== triage.identifier)
        .map((payment) =>
          transactionToTriageListItem(
            payment,
            selections.transactionIdentifiers ?? [],
            compareAmount,
            triage.effectiveDate,
          ),
        );
    const documents = foundDocuments.data?.documentsConnection?.data;
    const copies = foundCopies.data?.transactionsConnection?.data;
    const transfers = foundTransfers.data?.transactionsConnection?.data;
    // One row an account: the first way through it found, with the category
    // it files under where the way names one - one not made yet made under it
    // as it is booked.
    const throughAccount = new Set<string>();
    const ways = (foundWays.data?.waysToBook?.data ?? []).flatMap(
      (way): TriageOptionListItem[] => {
        const account = way.chartedAccount;
        if (account?.identifier) {
          if (throughAccount.has(account.identifier)) return [];
          throughAccount.add(account.identifier);
          return [
            {
              ...accountToListItem(
                account,
                selections.chartedAccountIdentifier,
              ),
              foundCategory: way.category
                ? { ...way.category, name: way.category.name ?? "" }
                : null,
              ...(way.categoryName && !way.category
                ? {
                    way: {
                      catalogueKey: null,
                      chartedAccountIdentifier: account.identifier,
                      categoryName: way.categoryName,
                    },
                  }
                : {}),
            },
          ];
        }
        // The catalogue's account, not made yet: named as it would be, under
        // the business's Expenses, where it is made as it is picked.
        return [
          {
            key: `way:${way.key}`,
            sectionId: "new-ways",
            selectionMode: "single",
            title: `Expenses / ${way.accountName} (new)`,
            selected: false,
            way: {
              catalogueKey: way.catalogueKey ?? null,
              chartedAccountIdentifier: null,
              categoryName: way.categoryName ?? null,
            },
          },
        ];
      },
    );
    return {
      match: shown(
        "match",
        (documents?.edges ?? []).map((edge) =>
          documentToTriageListItem(
            edge.node,
            selections.documentIdentifiers,
            compareAmount,
            triage.effectiveDate,
          ),
        ),
        documents?.pageInfo.hasNextPage ?? false,
      ),
      // A copy is looked at, not picked: it is booked as a duplicate by the
      // suggestion that says so.
      duplicate: shown(
        "duplicate",
        payments(copies).map((row) => ({
          ...row,
          key: `duplicate-of:${row.key.slice("transaction:".length)}`,
          sectionId: "duplicates",
          selected: false,
        })),
        copies?.pageInfo.hasNextPage ?? false,
      ),
      transfer: shown(
        "transfer",
        payments(transfers),
        transfers?.pageInfo.hasNextPage ?? false,
      ),
      post: shown("post", ways, false),
    };
  }, [
    foundWays.data,
    foundCopies.data,
    foundDocuments.data,
    foundTransfers.data,
    searches,
    selections.chartedAccountIdentifier,
    selections.documentIdentifiers,
    selections.transactionIdentifiers,
    triage.amount,
    triage.effectiveDate,
    triage.identifier,
  ]);
  // What is on screen: what the last search found, on every card at once -
  // changed together once every search for the words is in, not each as
  // its own comes back, nor emptied while it is asked again.
  const [shownSearch, setShownSearch] = useState<{
    searches: Partial<typeof searches>;
    mailboxes: boolean;
    found: Record<string, TriageOptionListItem[]>;
  }>({ searches: {}, mailboxes: true, found: {} });
  const isSettled =
    !foundDocuments.loading &&
    !foundCopies.loading &&
    !foundTransfers.loading &&
    !foundAccounts.loading &&
    !foundWays.loading;
  if (
    isSettled &&
    (shownSearch.searches !== searches ||
      shownSearch.mailboxes !== searchesMailboxes ||
      shownSearch.found !== foundByGroup)
  ) {
    setShownSearch({
      searches,
      mailboxes: searchesMailboxes,
      found: foundByGroup,
    });
  }

  // What is found is picked as it is from its own list, so Book knows it.
  useLayoutEffect(() => {
    const edges = foundDocuments.data?.documentsConnection?.data?.edges ?? [];
    if (edges.length > 0) {
      registerCatalogDocuments(edges.map((edge) => edge.node));
    }
  }, [foundDocuments.data, registerCatalogDocuments]);
  useLayoutEffect(() => {
    const edges =
      foundTransfers.data?.transactionsConnection?.data?.edges ?? [];
    if (edges.length > 0) {
      registerCatalogTransactions(edges.map((edge) => edge.node));
    }
  }, [foundTransfers.data, registerCatalogTransactions]);
  useLayoutEffect(() => {
    const accounts = foundAccounts.data?.chartedAccounts?.data ?? [];
    if (accounts.length === 0) return;
    registerCatalogChartedAccounts(
      accounts.flatMap((account) =>
        account.identifier
          ? [
              {
                id: account.id,
                identifier: account.identifier,
                name: account.name,
                path: account.path,
                fullPath: account.fullPath,
                description: account.description,
                categories: account.categories,
              },
            ]
          : [],
      ),
    );
  }, [foundAccounts.data, registerCatalogChartedAccounts]);

  // First, the drafts in review in a chat that the payment would take - a
  // receipt forwarded to its business and read by the AI, say. Read from each
  // draft's end, as its offers weigh it (``DocumentsService.offers``):
  // nothing of one is booked until it is accepted, and then it is the
  // suggestion it makes that is booked. One confirmed in its chat is that
  // suggestion as the confirm returns, and gives way to it.
  const drafts = useBackendQuery(Queries.TRIAGE_DRAFT_MATCHES_QUERY, {
    variables: {
      id: identifierAndTypenameToId(
        triage.identifier ?? "",
        "TransactionObject",
      ),
    },
    fetchPolicy: "cache-first",
    // Asked afresh when read again, as the payment's suggestions are.
    context: { queryDeduplication: false },
    skip: !triage.identifier,
  });
  const isDraftsLoading = drafts.loading && !drafts.data;
  const draftItems = useMemo((): TriageOptionListItem[] => {
    const node = drafts.data?.node;
    const offered = new Set(
      proposals.flatMap((suggestion) =>
        (suggestion.ruleSuggestionDocumentOptions ?? []).map(
          (option) => option.document?.identifier,
        ),
      ),
    );
    return (node && "draftMatches" in node ? node.draftMatches : []).flatMap(
      (match): TriageOptionListItem[] =>
        match.document && !offered.has(match.document.identifier)
          ? [
              {
                key: `draft:${match.document.identifier}`,
                sectionId: "drafts",
                selectionMode: "single",
                title: match.document.name ?? "Document",
                proposalGrouped: true,
                selected:
                  selections.draftDocumentIdentifier ===
                  match.document.identifier,
                draft: match,
              },
            ]
          : [],
    );
  }, [drafts.data, proposals, selections.draftDocumentIdentifier]);

  // What the viewer's searches of their mailboxes found for it: each a stop on
  // the mailbox card, looked at before the AI reads any of them.
  const results = useBackendQuery(
    Queries.TRIAGE_EMAIL_CONNECTION_SEARCH_RESULTS_QUERY,
    {
      variables: { id: triage.id },
      fetchPolicy: "cache-first",
      skip: !triage.id,
    },
  );
  // The words are looked for in the mailboxes as well, by the Match card,
  // when it is the card looked on: once for each search, on each payment
  // - what was found for it is kept, to show again.
  // Where there is a mailbox to look in.
  const mailboxSearchTerm =
    searches.match.term && searchesMailboxes && searchableMailboxes.length > 0
      ? searches.match.term
      : "";
  // On screen, the Match card's mailboxes looked in for what was typed.
  const shownMatchTerm = shownSearch.searches.match?.term ?? "";
  const shownInMailboxes = Boolean(shownMatchTerm) && shownSearch.mailboxes;
  const searchedMailboxesFor = useRef(new Set<string>());
  useEffect(() => {
    if (!mailboxSearchTerm || !triage.id) return;
    const asked = `${triage.id}:${mailboxSearchTerm}`;
    if (searchedMailboxesFor.current.has(asked)) return;
    searchedMailboxesFor.current.add(asked);
    searchMailbox(null, mailboxSearchTerm);
  }, [mailboxSearchTerm, searchMailbox, triage.id]);
  const foundStops = useMemo((): TriageOptionListItem[] => {
    const node = results.data?.node;
    const found =
      node && "emailConnectionSearchResults" in node
        ? node.emailConnectionSearchResults
        : [];
    // Gone as one of them is taken: it is read into a draft in its place.
    if (importingEmailConnectionSearchResult) {
      return [];
    }
    // What the payment's own terms found - or, the mailboxes searched for
    // what is typed, what those words found.
    const query = shownInMailboxes ? shownMatchTerm : "";
    return found
      .filter((result) => result.query === query)
      .map(
        (result): TriageOptionListItem => ({
          key: `email-connection-search-result:${result.identifier}`,
          sectionId: "email-connection-search-results",
          selectionMode: "single",
          title: result.filename || result.subject,
          selected: false,
          searchResult: result,
        }),
      );
  }, [
    importingEmailConnectionSearchResult,
    results.data,
    shownInMailboxes,
    shownMatchTerm,
  ]);

  // Read with nothing filed for the payment, in a chat it can be put right
  // in: its card stays where it was read, marked, with the way into that chat
  // - until a draft is in, another is imported, or the payment is left.
  const [unfiled, setUnfiled] = useState<{
    payment: string;
    read: NonNullable<typeof importingEmailConnectionSearchResult>;
    thread: { id: string; identifier: string; name?: string | null };
    draftsBefore: Set<string>;
  } | null>(null);
  const unfiledHere = unfiled?.payment === triage.identifier ? unfiled : null;

  // What was imported from a mailbox, being read in its chat: where it was,
  // and under the key the keyboard was on it by, the draft it is read into -
  // drawn from what the search said of it, until that draft is in.
  // Opened again while it is read - the page reloaded, or the payment left
  // and come back to - the file the server says is being read for it, as long
  // as the payment is at work.
  const draftsNode = drafts.data?.node;
  const beingRead =
    isProcessing && draftsNode && "readingFile" in draftsNode
      ? draftsNode.readingFile
      : null;
  const importingItems = useMemo((): TriageOptionListItem[] => {
    const result = importingEmailConnectionSearchResult ?? unfiledHere?.read;
    const read = result
      ? {
          key: `email-connection-search-result:${result.identifier}`,
          identifier: result.identifier,
          title: result.isEmail
            ? result.subject || "No subject"
            : result.filename,
          amountTotal: result.amount,
          dateIssued: result.sentAt,
        }
      : beingRead
        ? {
            key: `reading:${beingRead.identifier}`,
            identifier: beingRead.identifier,
            title: beingRead.name,
            amountTotal: null,
            dateIssued: null,
          }
        : null;
    if (!read) {
      return [];
    }
    return [
      {
        key: read.key,
        sectionId: "drafts",
        selectionMode: "single",
        title: read.title,
        proposalGrouped: true,
        selected: false,
        ...(result ? { searchResult: result } : { isBeingRead: true }),
        draft: {
          confidence: null,
          document: {
            id: read.identifier,
            identifier: read.identifier,
            name: read.title,
            amountTotal: read.amountTotal,
            dateIssued: read.dateIssued,
            fromEmailAddress: null,
            externalProvider: null,
            // Not drafted yet.
            pendingChange: null,
            entity: null,
          },
          // Read with nothing filed: the chat it was read in, to put it right.
          thread:
            result && !importingEmailConnectionSearchResult
              ? (unfiledHere?.thread ?? null)
              : null,
        } as unknown as TriageOptionListItem["draft"],
      },
    ];
  }, [beingRead, importingEmailConnectionSearchResult, unfiledHere]);
  // A draft in after all - put right in its chat - takes the marked card's
  // place, the keyboard on to it; another import, or another payment, and it
  // goes.
  useEffect(() => {
    if (!unfiled) return;
    const arrived = draftItems.find(
      (item) => !unfiled.draftsBefore.has(item.key),
    );
    if (
      arrived ||
      importingEmailConnectionSearchResult ||
      unfiled.payment !== triage.identifier
    ) {
      setUnfiled(null);
      if (
        arrived &&
        activeOptionKey ===
          `email-connection-search-result:${unfiled.read.identifier}`
      ) {
        setActiveOptionKey(arrived.key);
      }
    }
  }, [
    activeOptionKey,
    draftItems,
    importingEmailConnectionSearchResult,
    setActiveOptionKey,
    triage.identifier,
    unfiled,
  ]);
  // The draft in - one the payment was not offered when the import began: the
  // keyboard goes on to it from what it was read from. Read with nothing for
  // this payment - the payment at work, then let go - said, and the card goes.
  // Let go, the drafts are asked for once more before that is said: what the
  // chat filed is weighed for the payment before it is let go, but word of it
  // can come after.
  const hasReadRef = useRef(false);
  const draftsBeforeRef = useRef<Set<string> | null>(null);
  const importingRef = useRef(importingEmailConnectionSearchResult);
  importingRef.current = importingEmailConnectionSearchResult;
  const refetchDrafts = drafts.refetch;
  useEffect(() => {
    if (!importingEmailConnectionSearchResult) {
      hasReadRef.current = false;
      draftsBeforeRef.current = null;
      return;
    }
    draftsBeforeRef.current ??= new Set(draftItems.map((item) => item.key));
    if (isProcessing) hasReadRef.current = true;
    const importing = `email-connection-search-result:${importingEmailConnectionSearchResult.identifier}`;
    const arrived = draftItems.find(
      (item) => !draftsBeforeRef.current?.has(item.key),
    );
    if (arrived) {
      setImportingEmailConnectionSearchResult(null);
      if (activeOptionKey === importing) setActiveOptionKey(arrived.key);
    } else if (hasReadRef.current && !isProcessing && !isDraftsLoading) {
      hasReadRef.current = false;
      const read = importingEmailConnectionSearchResult;
      const before = draftsBeforeRef.current;
      const payment = triage.identifier ?? "";
      const sayNothingCame = (
        drafted = false,
        thread: NonNullable<typeof unfiled>["thread"] | null = null,
      ) => {
        // Its draft came with the drafts asked for, and goes in its place.
        if (drafted || importingRef.current !== read) return;
        setImportingEmailConnectionSearchResult(null);
        // With its chat to put it right in, its card stays, marked, rather
        // than going with a word on its way.
        if (thread) {
          setUnfiled({ payment, read, thread, draftsBefore: before });
          return;
        }
        State.getState().setSnackbarMessage(
          "Read in its chat, which filed nothing for this payment. It is in your notifications.",
        );
      };
      void refetchDrafts().then(
        ({ data }) => {
          const node =
            data?.node && "draftMatches" in data.node ? data.node : null;
          sayNothingCame(
            (node?.draftMatches ?? []).some(
              (match) =>
                match.document &&
                !before.has(`draft:${match.document.identifier}`),
            ),
            node?.readingThread ?? null,
          );
        },
        () => sayNothingCame(),
      );
    }
  }, [
    activeOptionKey,
    draftItems,
    importingEmailConnectionSearchResult,
    isDraftsLoading,
    isProcessing,
    refetchDrafts,
    setActiveOptionKey,
    setImportingEmailConnectionSearchResult,
    triage.identifier,
  ]);

  const listItems = useMemo(
    // Searched for or not, a card keeps what it suggests: what its words
    // found is beside it, and no row the keyboard is on is taken away.
    () => buildChangelistProposalItems(proposals, triage, selections),
    [proposals, selections, triage],
  );
  // The mailboxes still being searched for what is on screen: the search not
  // yet asked for, or under way.
  const isSearchingMailboxes =
    shownInMailboxes &&
    searchableMailboxes.length > 0 &&
    (!searchedMailboxesFor.current.has(`${triage.id}:${shownMatchTerm}`) ||
      searching?.query === shownMatchTerm);
  // Match's own actions, in its menu in the toolbar (O): each mailbox a search
  // may look in for its business, always one more to connect, and last the
  // address to forward its receipt to instead.
  const mailboxActions = useMemo((): TriageOptionListItem[] => {
    if (!triage.identifier) return [];
    const owner = scope.entities?.find(
      (entity) => entity.identifier === business,
    );
    return [
      ...searchableMailboxes.map((mailbox) => ({
        key: `search-mailbox:${mailbox.identifier}`,
        sectionId: "search-mailbox",
        selectionMode: "single" as const,
        title: mailbox.emailAddress,
        selected: false,
        mailbox: {
          identifier: mailbox.identifier,
          emailAddress: mailbox.emailAddress,
          provider: mailbox.provider ?? null,
          ownerName: mailbox.ownerName ?? null,
          ownedByViewer: mailbox.ownedByViewer,
          isSignedOut: mailbox.status !== EmailConnectionStatusEnum.Connected,
          searchFailure: mailbox.lastSearchFailure ?? null,
        },
      })),
      {
        key: "search-mailbox:connect",
        sectionId: "search-mailbox",
        selectionMode: "single" as const,
        title:
          searchableMailboxes.length > 0
            ? "Connect another mailbox"
            : "Connect Gmail or Outlook",
        selected: false,
        mailbox: null,
      },
      {
        key: `forward:${business ?? "loading"}`,
        sectionId: "search-mailbox",
        selectionMode: "single" as const,
        title: "Copy forwarding address",
        selected: false,
        ...(owner
          ? {
              business: {
                entityIdentifier: owner.identifier,
                name: owner.name ?? "",
                address: owner.inboundForwardingAddress ?? null,
              },
            }
          : {}),
      },
    ];
  }, [business, scope.entities, searchableMailboxes, triage.identifier]);
  const mailboxStops = useMemo((): TriageOptionListItem[] => {
    if (!triage.identifier) return [];
    const stop = (
      key: string,
      sectionId: string,
      title: string,
      extra: Partial<TriageOptionListItem> = {},
    ): TriageOptionListItem => ({
      key,
      sectionId,
      selectionMode: "single",
      title,
      selected: false,
      ...extra,
    });
    // What a search found: the three it puts first - those that say the most
    // of the payment - and the rest behind a stop that opens them all, on the
    // first of the rest.
    const shownFound =
      foundStops.length > 3
        ? [
            ...foundStops.slice(0, 3),
            stop(
              "email-connection-search-result:more",
              "email-connection-search-results",
              `${foundStops.length - 3} more`,
              { searchResult: foundStops[3].searchResult },
            ),
          ]
        : foundStops;
    // Searched for what is typed: rows drawn loading on the card till it is
    // back, then what it found - or a row that says it found nothing, and
    // offers another mailbox to connect. With none to look in, the way to
    // connect one.
    if (shownInMailboxes) {
      if (isSearchingMailboxes) return [];
      // Not run - Nango could not load a mailbox, or one refused: said so,
      // and why, to search again - not that nothing in them says it.
      if (searchFailure && (searchFailure.query ?? "") === shownMatchTerm) {
        return [
          stop(
            "search-mailbox:failed",
            "search-mailbox",
            "Couldn't search your mailboxes",
            {
              mailbox: null,
              searchedFor: shownSearch.searches.match?.words,
              searchFailure: searchFailure.message,
            },
          ),
        ];
      }
      // A mailbox the search could not run in, after what the rest found: why,
      // and to search it again.
      const unsearched = mailboxActions.filter(
        (action) => action.mailbox?.searchFailure,
      );
      return [
        ...(shownFound.length > 0
          ? shownFound
          : [
              stop(
                "search-mailbox:connect",
                "search-mailbox",
                "Connect Gmail or Outlook",
                searchableMailboxes.length > 0
                  ? {
                      mailbox: null,
                      searchedFor: shownSearch.searches.match?.words,
                      someUnsearched: unsearched.length > 0,
                    }
                  : { mailbox: null },
              ),
            ]),
        ...unsearched,
      ];
    }
    // What a search found: Match's own actions - each mailbox to search, one
    // more to connect, the address to forward its receipt to - are in its
    // menu in the toolbar (``mailboxActions``).
    return shownFound;
  }, [
    foundStops,
    isSearchingMailboxes,
    mailboxActions,
    searchFailure,
    searchableMailboxes.length,
    shownInMailboxes,
    shownMatchTerm,
    shownSearch.searches.match,
    triage.identifier,
  ]);
  // What is suggested, by kind, each a card of its own - on Match first the
  // drafts in review it would take - and after a kind's, a card of what its
  // words found, headed by its kind: on Match, what a search of the
  // mailboxes came to too. Each says the words it is searched for, and spins
  // in its corner till what they found is on screen.
  const items = useMemo(
    () =>
      TRIAGE_SUGGESTION_GROUPS.flatMap((group): TriageOptionListItem[] => {
        const id = group.id;
        const found = [
          ...(shownSearch.found[id] ?? []),
          ...(id === "match" ? mailboxStops : []),
        ];
        const isSearching =
          (Boolean(searches[id].term) &&
            shownSearch.searches[id]?.term !== searches[id].term) ||
          (id === "match" && isSearchingMailboxes);
        return [
          ...(id === "match" ? [...draftItems, ...importingItems] : []).map(
            (stop): TriageOptionListItem => ({
              ...stop,
              selected: false,
              group,
              alternatives: [stop],
            }),
          ),
          ...listItems.filter((item) => item.group?.id === id),
          ...(searches[id].term || found.length > 0 || isSearching
            ? [
                {
                  key: `group:${id}`,
                  sectionId: "lines",
                  selectionMode: "single" as const,
                  title: group.title,
                  selected: false,
                  group,
                  isHeaded: true,
                  // Nothing in yet: the card its own stop.
                  alternatives: found.length > 0 ? found : undefined,
                  isSearching,
                  searchedFor:
                    searches[id].words.length > 0
                      ? searches[id].words
                      : undefined,
                  mailboxSearch:
                    id === "match" &&
                    shownInMailboxes &&
                    searchableMailboxes.length > 0
                      ? {
                          term: shownMatchTerm,
                          isSearching: isSearchingMailboxes,
                        }
                      : undefined,
                },
              ]
            : []),
        ];
      }),
    [
      draftItems,
      importingItems,
      isSearchingMailboxes,
      listItems,
      mailboxStops,
      searchableMailboxes.length,
      searches,
      shownInMailboxes,
      shownMatchTerm,
      shownSearch,
    ],
  );
  // Each kind's own actions, on its button in the toolbar: its suggestions
  // worked out again, and the list of all it searches opened to pick from by
  // hand - till the payment is booked, and has nothing left to suggest or
  // pick - after, on Match, its mailboxes.
  const activeItem = items.find((item) =>
    (item.alternatives ?? [item]).some((stop) => stop.key === activeOptionKey),
  );
  const isNothingPicked =
    selections.linesSuggestionIdentifier == null &&
    !(activeItem?.alternatives ?? []).some((way) => way.selected);
  const groupMenus = useMemo(
    () =>
      TRIAGE_SUGGESTION_GROUPS.map((group): TriageGroupMenu => {
        const cardActions: TriageOptionListItem[] = triage.isBooked
          ? []
          : [
              {
                key: `rerun:${group.id}`,
                sectionId: "card-actions",
                selectionMode: "single",
                title: "Think",
                selected: false,
                group,
              },
              {
                key: `browse:${group.id}`,
                sectionId: "card-actions",
                selectionMode: "single",
                title: TRIAGE_TAB_CATALOG_CONFIG[group.tab].searchPlaceholder,
                selected: false,
                group,
              },
            ];
        const isFocused = activeItem?.group?.id === group.id;
        return {
          group,
          actions:
            group.id === "match"
              ? [...mailboxActions, ...cardActions]
              : cardActions,
          // Match spins too while a mailbox its menu searched is.
          isBusy:
            rerunningSuggestions.includes(group.id) ||
            (group.id === "match" && searching?.connectionIdentifier != null),
          isFocused,
          isThinkingByKey: isFocused && isNothingPicked,
        };
      }),
    [
      activeItem?.group?.id,
      isNothingPicked,
      mailboxActions,
      rerunningSuggestions,
      searching?.connectionIdentifier,
      triage.isBooked,
    ],
  );
  useLayoutEffect(() => {
    setGroupMenus?.(groupMenus);
  }, [groupMenus, setGroupMenus]);
  useEffect(() => () => setGroupMenus?.(null), [setGroupMenus]);

  // The keyboard stepped through the stops from the panel's search, as the
  // arrow keys step it in the list: from where it is, or - nowhere yet - onto
  // the first, or the last.
  useEffect(() => {
    registerStepSuggestions?.((by) => {
      const stops = items.flatMap((item) =>
        (item.alternatives ?? [item]).map((stop) => stop.key),
      );
      if (stops.length === 0) return;
      const at = activeOptionKey ? stops.indexOf(activeOptionKey) : -1;
      const next =
        at < 0
          ? by > 0
            ? 0
            : stops.length - 1
          : Math.min(Math.max(at + by, 0), stops.length - 1);
      setActiveOptionKey(stops[next]);
    });
  }, [activeOptionKey, items, registerStepSuggestions, setActiveOptionKey]);

  // The keys in the order last shown, each row's stops: what took the place of
  // one that went. Kept after the effect below reads it.
  const shownKeysRef = useRef<string[][]>([]);
  // A draft the keyboard was on that went before what it makes came - its
  // document, and the stop the keyboard was moved to in its place: followed on
  // to what it makes, if the person is still there.
  const leftDraftRef = useRef<{ document: string; landed: string } | null>(
    null,
  );

  // Keyboard-highlight the first proposal when the panel engages - nothing
  // picked: Space / Enter / click picks.
  // Not while a list is open to pick from by hand: the keyboard is on its rows.
  useEffect(() => {
    if (!suggestionsEngaged || reconcilingManually) {
      return;
    }
    // A card's first stop: the card itself is none, but where it has none.
    const firstKey = (items[0]?.alternatives ?? items)[0]?.key ?? null;
    if (!firstKey) {
      return;
    }
    if (activeOptionKey == null) {
      // On it, nothing picked: a suggestion is picked by the person alone -
      // what the payment's row stages is what they chose.
      setActiveOptionKey(firstKey);
      return;
    }
    // The keyboard on one of a card's stops is where it is.
    const visibleKeys = new Set(
      items.flatMap((item) =>
        (item.alternatives ?? [item]).map((stop) => stop.key),
      ),
    );
    if (
      !visibleKeys.has(activeOptionKey) &&
      foundStops.some((stop) => stop.key === activeOptionKey)
    ) {
      // Found, but behind the stop that opens the rest - stepped onto in their
      // dialog: the keyboard is on that stop.
      setActiveOptionKey("email-connection-search-result:more");
    } else if (!visibleKeys.has(activeOptionKey)) {
      // Gone from the list - turned down, or worked out again: the keyboard
      // moves to what took its place, and nothing is picked for it.
      const was = shownKeysRef.current.findIndex((stops) =>
        stops.includes(activeOptionKey),
      );
      const near = items[Math.min(Math.max(was, 0), items.length - 1)];
      const landed =
        (near?.alternatives ?? (near ? [near] : []))[0]?.key ?? firstKey;
      leftDraftRef.current = activeOptionKey.startsWith("draft:")
        ? { document: activeOptionKey.slice("draft:".length), landed }
        : null;
      setActiveOptionKey(landed);
    }
  }, [
    activeOptionKey,
    foundStops,
    items,
    reconcilingManually,
    setActiveOptionKey,
    suggestionsEngaged,
  ]);
  useEffect(() => {
    shownKeysRef.current = items.map((item) =>
      (item.alternatives ?? [item]).map((stop) => stop.key),
    );
  });

  // A draft confirmed in its chat: the suggestion it makes takes its place -
  // picked, if the draft was - and the keyboard with it, if it was there,
  // picked or only open.
  useEffect(() => {
    const picked = selections.draftDocumentIdentifier;
    const left = leftDraftRef.current;
    const onDraft = activeOptionKey?.startsWith("draft:")
      ? activeOptionKey.slice("draft:".length)
      : left && activeOptionKey === left.landed
        ? left.document
        : null;
    const madeOf = (identifier: string | null | undefined) =>
      identifier
        ? proposals.find((suggestion) =>
            (suggestion.ruleSuggestionDocumentOptions ?? []).some(
              (option) => option.document?.identifier === identifier,
            ),
          )
        : undefined;
    const madePicked = madeOf(picked);
    if (madePicked) toggleLinesSuggestion(madePicked, null, setSelection);
    const madeOn = madeOf(onDraft);
    if (madeOn) {
      leftDraftRef.current = null;
      setActiveOptionKey(`lines:${madeOn.identifier}`);
    }
  }, [
    activeOptionKey,
    proposals,
    selections.draftDocumentIdentifier,
    setActiveOptionKey,
    setSelection,
  ]);

  // Drawn mirrored - facing the payments - as the line-up is, when it is: a
  // card the same in either.
  const isMirrored = State((state) => state.isTriageMirrored);
  return (
    <div
      data-testid="triage-changelist-proposals"
      data-triage-mirrored={isMirrored || undefined}
      className="h-full min-h-0 w-full flex flex-col overflow-hidden"
    >
      <div className="flex-1 min-h-0 w-full flex flex-col">
        {/* Drawn once the drafts it would take, what a search found for it
            and the mailboxes to search are known (the panel's one spinner
            till then) - else the ways to a receipt were drawn for one with a
            draft on its way, and taken off as it came. */}
        <TriageOptionList
          tab="suggestions"
          items={items}
          // Nothing suggested, nor searched for: said so - the toolbar's
          // kinds search by hand.
          emptyState={{ title: "No suggestions" }}
          data-testid="triage-suggestions-options"
        />
      </div>
      <TriageEmailConnectionSearchResultsDialog stops={foundStops} />
    </div>
  );
}
